import { useQuery } from '@tanstack/react-query'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from '../../../../../lib/axios'
import useAuth from '../../../../../hooks/useAuth'

import errorAnimation from '../../../../../assets/animations/error.json'
import lottie from 'lottie-web'
import { Toaster } from 'react-hot-toast'
import { Breadcrumb } from '../../../../../components/ui/breadcrumb'
import { formatCashAmount, isCoupureCN, isInterface } from '../../../../../lib/utils'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import Coupure from '../../../../../components/portal/coupure/Coupure'
import { format } from 'date-fns'
import { convertToDate } from '../../../../../lib/date'
import { Pagination } from '../../../../../components/ui/pagination'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import LoadingScreen from '../../../../../components/portal/feedback/LoadingScreen'
import { useTranslation } from 'react-i18next'
import { getOrderDraftState } from '../../../../../lib/general/params'

const Detail = () => {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const { t } = useTranslation()
  const { announcement } = useParams()
  
  const partners = useSelector((state:RootState)=>state.partners);
  const customers = useSelector((state:RootState)=>state.customers);
  const orderdraftstate = useSelector((state:RootState)=>state.orderdraftstate)

  const errorContainer = useRef() as MutableRefObject<HTMLDivElement>

  const [selected_index, setselected_index] = useState(0);

  const {data:detail, isFetching:isFetching_detail} = useQuery<AnnouncemenDetail[] | null>({
    queryKey: [`collections_detail_${announcement}`],
    queryFn: async () => {
      try {
        const {data} = await axios.post(`announcement/draft/detail`, {
          token: auth.data?.token,
          id: announcement
        })
        if(data.errorcode === 0){ return data.object.detail }
        else { throw new Error(data.object) }
      } catch (error) {
        return null
      }
    }
  })

  const {data:detail_info, isFetching:isFetching_info} = useQuery<AnnouncementDraftSearch | null>({
    queryKey: [`collections_detail_info_${announcement}`],
    queryFn: async () => {
      try {
        const {data} = await axios.post(`announcement/draft/search`, {
          token: auth.data?.token,
          id: announcement
        })
        if(data.errorcode === 0){ return data.object.announcementdraft[0] }
        else { throw new Error(data.message) }
      } catch (error) {
        return null
      }
    }
  })

  useEffect(()=>{
    if(!detail || !detail_info){
      const instance = lottie.loadAnimation({
        container: errorContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: errorAnimation
      })
      return () => instance.destroy()
    }
  }, [detail, detail_info])

  let relation = customers.find(obj => obj.eindklant.some(e => e.relnr === detail_info?.relnr))
  let partner = partners.find(obj => obj.relnr === relation?.id)
  let customer = customers.flatMap(obj => obj.eindklant).find(e => e.relnr === detail_info?.relnr)

  console.log(detail)
    
  return (
  <>
    <Toaster /> 

    {((isFetching_detail || isFetching_info) && (!isInterface(detail, {} as AnnouncemenDetail[]))) && 
    <LoadingScreen />
    }

    {(detail === null || detail_info === null) && 
    <div className={`flex flex-col items-center justify-center`}>
      <div className='w-[240px] h-[240px]' ref={errorContainer}></div>
      <h2 className='mb-4 text-2xl font-semibold'>Oeps!</h2>
      <p className='text-center'>De opgevraagde ophaling kon niet worden gevonden.<br></br>Gelieve deze pagina te bezoeken via de <Link to='/bestellen-en-ophalen/overzicht-ophalingen' className='font-medium underline'>overzicht ophalingen</Link> pagina.</p>
    </div>
    }

    {(isInterface(detail, {} as AnnouncemenDetail[]) && isInterface(detail_info, {} as AnnouncementDraftSearch)) && 
    <div className="relative w-full p-4 overflow-auto md:p-8">
      <div className='flex flex-col'>
          <Breadcrumb items={[{title:<p onClick={()=>{navigate('/bestellen-en-ophalen')}} className='cursor-pointer'>{t("order_and_pickup.o_overview_collections.link_orders")}</p>}, {title:<p onClick={()=>{navigate('/bestellen-en-ophalen/overzicht-ophalingen')}} className='cursor-pointer'>{t("order_and_pickup.o_overview_collections.link_collection")}</p>}, {title:`${t("order_and_pickup.o_overview_collections.collection")} ${announcement}`}]}/>
          <h1 className='text-2xl font-semibold'>{t("order_and_pickup.o_overview_collections.title_detail")}</h1>
      </div>

      <div className='flex flex-col gap-4 p-4 mt-4 border rounded-sm md:mt-8'>
        <h2 className='text-lg'>{t("order_and_pickup.o_overview_collections.title_detail")}</h2>
        <div className='grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.partner_nr")}</p>
            <p className='text-neutral-500'>{partner?.relnr}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.partner")}</p>
            <p className='text-neutral-500'>{partner?.name}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.relation_nr")}</p>
            <p className='text-neutral-500'>{detail_info.relnr}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.customer_p2")}</p>
            <p className='text-neutral-500'>{detail_info.name}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.customer_code")}</p>
            <p className='text-neutral-500'>{customer?.clientcode}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.amount")}</p>
            <p className='text-neutral-500'>{formatCashAmount(detail_info.amount)}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.valuta")}</p>
            <p className='text-neutral-500'>{detail_info.deviescurrencyname ?? 'EUR'}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.type")}</p>
            <p className='text-neutral-500'>{detail_info.cn.toLowerCase() === 'c' ? t("order_and_pickup.o_overview_collections.coins") : t("order_and_pickup.o_overview_collections.notes")}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.date_created")}</p>
            <p className='text-neutral-500'>{format(convertToDate(detail_info.dtcreation) || new Date(), "dd/MM/yyyy")}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.collection")}</p>
            <p className='text-neutral-500'>{format(convertToDate(detail_info.date) || new Date(), "dd/MM/yyyy")}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.keepsafe_nr")}</p>
            <p className='text-neutral-500'>{detail_info.keepsafe}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.status")}</p>
            <p className='text-neutral-500'>{orderdraftstate && getOrderDraftState(detail_info.status, orderdraftstate)?.description}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.reference")}</p>
            <p className='text-neutral-500'>{detail_info.reference}</p>
          </div>
          <div className='flex gap-2'>
            <p className='font-semibold'>{t("order_and_pickup.o_overview_collections.auto_ref")}</p>
            <p className='text-neutral-500'>{detail_info.autoreference}</p>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4 p-4 mt-4 border rounded-sm md:mt-8'>
        <div className='flex items-center justify-between'>
          <h2 className='text-lg'>{t("order_and_pickup.o_overview_collections.description")} {selected_index+1} {t("order_and_pickup.o_overview_collections.description_p2")} {detail.length}</h2>
          {detail.length > 1 && 
          <div className='flex gap-2'>
            <button onClick={()=>{setselected_index(prev=>prev-1)}} disabled={selected_index === 0} className='flex items-center justify-center duration-300 border rounded-full w-9 h-9 hover:bg-primary/10 hover:border-primary/10 disabled:opacity-50 disabled:cursor-not-allowed'><ChevronLeft className='w-5 text-neutral-500'/></button>
            <button onClick={()=>{setselected_index(prev=>prev+1)}} disabled={(selected_index+1) === detail.length} className='flex items-center justify-center duration-300 border rounded-full w-9 h-9 hover:bg-primary/10 hover:border-primary/10 disabled:opacity-50 disabled:cursor-not-allowed'><ChevronRight className='w-5 text-neutral-500'/></button>
          </div>
          }
        </div>
        
        <div className='border'>
          <div className='grid grid-cols-3 px-4 py-2 border-b bg-slate-100'>
            <i className='text-sm font-medium uppercase text-muted-foreground'></i>
            <p className='text-sm font-medium text-right uppercase text-muted-foreground'>{t("order_and_pickup.o_overview_collections.count")}</p>
            <p className='text-sm font-medium text-right uppercase text-muted-foreground'>{t("order_and_pickup.o_overview_collections.amount")}</p>
          </div>
          <div className='flex flex-col gap-4 p-4'>
            <Coupure cn={isCoupureCN(detail[selected_index]?.coupures)} coupures={detail[selected_index]?.coupures ?? []} currency={detail_info.deviescurrencyname} show_total/>
          </div>
        </div>
      </div>

    </div>
    }
    
  </>
  )
}

export default Detail